import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Tooltip,
  Chip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Divider
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { Badge } from '@mui/material';
import { format, startOfDay, endOfDay, isWithinInterval, isSameDay, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';
import { fr } from 'date-fns/locale';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { COLLECTIONS } from '../firebase/collections';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';

// Constantes pour les statuts et priorités
const STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
};

const STATUS_LABELS = {
  [STATUS.PENDING]: 'En attente',
  [STATUS.IN_PROGRESS]: 'En cours',
  [STATUS.COMPLETED]: 'Terminé',
  [STATUS.CANCELLED]: 'Annulé'
};

const STATUS_COLOR_CLASSES = {
  [STATUS.PENDING]: 'warning',
  [STATUS.IN_PROGRESS]: 'info',
  [STATUS.COMPLETED]: 'success',
  [STATUS.CANCELLED]: 'error'
};

const PRIORITY_LABELS = { low: 'Basse', medium: 'Moyenne', high: 'Haute', urgent: 'Urgente' };

const PRIORITY_COLOR_VALUES = {
  low: '#4caf50',     // vert
  medium: '#2196f3',  // bleu
  high: '#ff9800',    // orange
  urgent: '#f44336'   // rouge
};

export default function Calendar() {
  const { currentUser, userRole, userService, userSchool } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [workRequests, setWorkRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [datesWithRequests, setDatesWithRequests] = useState([]);
  const [requestsByDate, setRequestsByDate] = useState({});
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [periodFilter, setPeriodFilter] = useState('day'); // 'day', 'month', 'year'

  useEffect(() => {
    console.log('Calendar - useEffect déclenché', {
      currentUser: currentUser?.uid,
      userRole,
      userService: userService?.id,
      userSchool: userSchool?.id
    });
    fetchWorkRequests();
  }, [currentUser, userRole, userService, userSchool]);

  const fetchWorkRequests = async () => {
    try {
      console.log('Calendar - Début fetchWorkRequests');
      setLoading(true);
      setError('');

      let workRequestsQuery = collection(db, COLLECTIONS.WORK_REQUESTS);
      let constraints = [];

      // Filtrer selon le rôle et le service/école
      console.log('Calendar - Filtrage par rôle:', { userRole, userServiceId: userService?.id, userSchoolId: userSchool?.id });
      
      if (userRole === 'service') {
        console.log('Calendar - Filtrage pour service normal');
        constraints.push(where('requestingServiceId', '==', userService?.id));
      } else if (userRole === 'technical') {
        console.log('Calendar - Filtrage pour service technique');
        constraints.push(where('assignedServiceId', '==', userService?.id));
      } else if (userRole === 'requester') {
        console.log('Calendar - Filtrage pour demandeur (requester)');
        // Pour le rôle requester, on affiche les demandes créées par l'utilisateur
        if (currentUser) {
          console.log('Calendar - Filtrage par createdBy pour demandeur:', currentUser.uid);
          constraints.push(where('createdBy', '==', currentUser.uid));
        } else {
          console.log('Calendar - Demandeur non authentifié');
        }
      } else if (userSchool) {
        console.log('Calendar - Filtrage pour admin avec école');
        constraints.push(where('schoolId', '==', userSchool.id));
      } else {
        console.log('Calendar - Aucun filtre appliqué - cas par défaut');
      }

      console.log('Calendar - Contraintes de requête:', constraints);
      const q = query(workRequestsQuery, ...constraints);
      const querySnapshot = await getDocs(q);
      
      console.log(`Calendar - Résultat de la requête: ${querySnapshot.docs.length} documents trouvés`);
      
      // Enrichir les demandes avec les informations du demandeur
      const requests = await Promise.all(
        querySnapshot.docs.map(async (docSnapshot) => {
          const data = docSnapshot.data();
          const enrichedData = {
            id: docSnapshot.id,
            ...data
          };

          // Récupérer le nom du demandeur
          if (data.requestingServiceId || data.createdBy) {
            try {
              const requesterId = data.requestingServiceId || data.createdBy;
              
              // Essayer d'abord dans la collection SERVICES
              let requesterDoc = await getDoc(doc(db, COLLECTIONS.SERVICES, requesterId));
              
              if (requesterDoc.exists()) {
                const requesterData = requesterDoc.data();
                enrichedData.requesterName = requesterData.name || requesterData.displayName || 'Service';
              } else {
                // Si non trouvé, essayer dans la collection users
                const userDoc = await getDoc(doc(db, 'users', requesterId));
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  enrichedData.requesterName = userData.displayName || userData.name || userData.email || 'Utilisateur';
                }
              }
            } catch (error) {
              console.error('Calendar - Erreur récupération demandeur:', error);
              enrichedData.requesterName = 'Non spécifié';
            }
          } else {
            enrichedData.requesterName = 'Non spécifié';
          }

          return enrichedData;
        })
      );
      
      console.log('Calendar - Données des demandes enrichies:', requests);

      setWorkRequests(requests);
      
      // Collecter les dates uniques avec des demandes et organiser les demandes par date
      const uniqueDates = [];
      const requestsMap = {};
      
      console.log('Calendar - Début traitement des dates');
      
      requests.forEach(request => {
        console.log('Calendar - Traitement de la demande:', { id: request.id, createdAt: request.createdAt });
        
        if (request.createdAt) {
          try {
            const requestDate = request.createdAt.toDate();
            const dateString = format(requestDate, 'yyyy-MM-dd');
            console.log('Calendar - Date formatée:', dateString);
            
            if (!uniqueDates.includes(dateString)) {
              uniqueDates.push(dateString);
              requestsMap[dateString] = {
                total: 0,
                byPriority: { urgent: 0, high: 0, medium: 0, low: 0 },
                byStatus: { pending: 0, inProgress: 0, completed: 0 }
              };
              console.log('Calendar - Nouvelle date ajoutée:', dateString);
            }
            
            // Incrémenter les compteurs
            requestsMap[dateString].total += 1;
            
            // Compter par priorité
            if (request.priority) {
              requestsMap[dateString].byPriority[request.priority] += 1;
            }
            
            // Compter par statut
            if (request.status === STATUS.PENDING) {
              requestsMap[dateString].byStatus.pending += 1;
            } else if (request.status === STATUS.IN_PROGRESS) {
              requestsMap[dateString].byStatus.inProgress += 1;
            } else if (request.status === STATUS.COMPLETED) {
              requestsMap[dateString].byStatus.completed += 1;
            }
          } catch (error) {
            console.error('Calendar - Erreur lors du traitement de la date:', error);
            console.error('Calendar - Données de la demande:', request);
          }
        } else {
          console.log('Calendar - Demande sans date de création:', request.id);
        }
      });
      
      console.log('Calendar - Dates uniques avec demandes:', uniqueDates);
      console.log('Calendar - Demandes par date:', requestsMap);
      
      setDatesWithRequests(uniqueDates);
      setRequestsByDate(requestsMap);
      
      setLoading(false);
    } catch (err) {
      console.error('Calendar - Erreur lors de la récupération des demandes:', err);
      console.error('Calendar - Stack trace:', err.stack);
      setError('Erreur lors de la récupération des demandes: ' + err.message);
      setLoading(false);
    } finally {
      console.log('Calendar - Fin fetchWorkRequests');
    }
  };

  const getRequestsForDate = (date) => {
    console.log('Calendar - Recherche des demandes pour la période:', periodFilter);
    
    const filteredRequests = workRequests.filter(request => {
      if (!request.createdAt) {
        console.log('Calendar - Demande sans date de création:', request.id);
        return false;
      }
      
      try {
        const requestDate = request.createdAt.toDate();
        
        if (periodFilter === 'day') {
          return isSameDay(requestDate, date);
        } else if (periodFilter === 'month') {
          const monthStart = startOfMonth(date);
          const monthEnd = endOfMonth(date);
          return isWithinInterval(requestDate, { start: monthStart, end: monthEnd });
        } else if (periodFilter === 'year') {
          const yearStart = startOfYear(date);
          const yearEnd = endOfYear(date);
          return isWithinInterval(requestDate, { start: yearStart, end: yearEnd });
        }
        return false;
      } catch (error) {
        console.error('Calendar - Erreur lors du filtrage:', error);
        console.error('Calendar - Données de la demande:', request);
        return false;
      }
    });
    
    console.log(`Calendar - ${filteredRequests.length} demandes trouvées pour la période`);
    return filteredRequests.sort((a, b) => a.createdAt.toDate() - b.createdAt.toDate());
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
  };

  const handleRequestClick = (request) => {
    setSelectedRequest(request);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Calendrier des demandes
          </Typography>
          
          <ToggleButtonGroup
            value={periodFilter}
            exclusive
            onChange={(e, newValue) => newValue && setPeriodFilter(newValue)}
            size="small"
            color="primary"
          >
            <ToggleButton value="day">Jour</ToggleButton>
            <ToggleButton value="month">Mois</ToggleButton>
            <ToggleButton value="year">Année</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                <DateCalendar
                  value={selectedDate}
                  onChange={handleDateClick}
                  sx={{ width: '100%' }}
                  dayOfWeekFormatter={(day) => day.charAt(0).toUpperCase() + day.slice(1, 3)}
                  slots={{
                    day: (dayProps) => {
                      const { day, outsideCurrentMonth, ...other } = dayProps;
                      const dateString = format(day, 'yyyy-MM-dd');
                      const hasRequests = datesWithRequests.includes(dateString);
                      const dateRequests = hasRequests ? requestsByDate[dateString] : null;
                      
                      // Préparer le contenu du tooltip
                      let tooltipContent = '';
                      if (dateRequests) {
                        tooltipContent = `${dateRequests.total} demande(s)\n`;
                        
                        if (dateRequests.byStatus.pending > 0) {
                          tooltipContent += `${dateRequests.byStatus.pending} en attente\n`;
                        }
                        if (dateRequests.byStatus.inProgress > 0) {
                          tooltipContent += `${dateRequests.byStatus.inProgress} en cours\n`;
                        }
                        if (dateRequests.byStatus.completed > 0) {
                          tooltipContent += `${dateRequests.byStatus.completed} terminée(s)`;
                        }
                      }
                      
                      return (
                        <Tooltip 
                          title={tooltipContent} 
                          placement="top"
                          arrow
                          disableHoverListener={!hasRequests || outsideCurrentMonth}
                        >
                          <Badge
                            key={day.toString()}
                            overlap="circular"
                            badgeContent={hasRequests && !outsideCurrentMonth ? '●' : undefined}
                            color="primary"
                          >
                            <PickersDay
                              {...other}
                              outsideCurrentMonth={outsideCurrentMonth}
                              day={day}
                              sx={{
                                ...(hasRequests && !outsideCurrentMonth && {
                                  backgroundColor: 'primary.light',
                                  color: 'primary.contrastText',
                                  fontWeight: 'bold',
                                  '&:hover': {
                                    backgroundColor: 'primary.main',
                                  },
                                  '&.Mui-selected': {
                                    backgroundColor: 'primary.dark',
                                  }
                                })
                              }}
                            />
                          </Badge>
                        </Tooltip>
                      );
                    }
                  }}
                />
              </LocalizationProvider>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {periodFilter === 'day' && `Demandes du ${format(selectedDate, 'dd MMMM yyyy', { locale: fr })}`}
                  {periodFilter === 'month' && `Demandes de ${format(selectedDate, 'MMMM yyyy', { locale: fr })}`}
                  {periodFilter === 'year' && `Demandes de l'année ${format(selectedDate, 'yyyy', { locale: fr })}`}
                </Typography>
                <Chip 
                  label={`${getRequestsForDate(selectedDate).length}`} 
                  color="primary" 
                  size="small"
                />
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Box sx={{ mt: 2, maxHeight: 500, overflowY: 'auto' }}>
                {getRequestsForDate(selectedDate).length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                    Aucune demande pour cette période
                  </Typography>
                ) : (
                  getRequestsForDate(selectedDate).map((request) => (
                    <Box
                      key={request.id}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        '&:hover': {
                          bgcolor: 'action.hover',
                          cursor: 'pointer'
                        }
                      }}
                      onClick={() => handleRequestClick(request)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle2" noWrap>
                            {request.title}
                          </Typography>
                          {periodFilter !== 'day' && request.createdAt && (
                            <Typography variant="caption" color="text.secondary">
                              {format(request.createdAt.toDate(), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                            </Typography>
                          )}
                        </Box>
                        <Tooltip title="Voir les détails">
                          <IconButton size="small">
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={STATUS_LABELS[request.status]}
                          color={STATUS_COLOR_CLASSES[request.status]}
                          size="small"
                        />
                        {request.priority && (
                          <Chip
                            label={PRIORITY_LABELS[request.priority] || request.priority}
                            sx={{ bgcolor: PRIORITY_COLOR_VALUES[request.priority] || '#1976d2', color: 'white' }}
                            size="small"
                          />
                        )}
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {selectedRequest?.title}
          </DialogTitle>
          <DialogContent>
            {selectedRequest && (
              <Box sx={{ p: 2 }}>
                <Typography variant="body1" paragraph>
                  {selectedRequest.description}
                </Typography>
                
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Date de création
                    </Typography>
                    <Typography variant="body2">
                      {selectedRequest.createdAt ? format(selectedRequest.createdAt.toDate(), 'dd/MM/yyyy HH:mm') : 'Non spécifiée'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Demandeur
                    </Typography>
                    <Typography variant="body2">
                      {selectedRequest.requesterName || 'Non spécifié'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Statut
                    </Typography>
                    <Chip
                      label={STATUS_LABELS[selectedRequest.status]}
                      color={STATUS_COLOR_CLASSES[selectedRequest.status]}
                      size="small"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Priorité
                    </Typography>
                    {selectedRequest.priority && (
                      <Chip
                        label={PRIORITY_LABELS[selectedRequest.priority] || selectedRequest.priority}
                        sx={{ bgcolor: PRIORITY_COLOR_VALUES[selectedRequest.priority] || '#1976d2', color: 'white' }}
                        size="small"
                      />
                    )}
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>
              Fermer
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
}
